import './MultiSelect.css';

import { useMemo } from 'react';
import {
  MultiSelectChangeEvent,
  MultiSelect as PrimeMultiSelect,
} from 'primereact/multiselect';
import { ChipRemoveEvent } from 'primereact/chip';
import type {
  FilterError,
  StaticControlItem,
} from '../../../../../types/types';
import { useTranslate } from '../../../../../hooks/context/useTranslate';
import { MAX_SELECTED_LABELS_FOR_MULTISELECT } from '../../../../../constants';
import useChipsScrollbarDetection from '../../../../../hooks/useChipsScrollbarDetection';
import Chip from './components/Chip/Chip';
import InputErrorMessage from '../../../InputErrorMessage/InputErrorMessage';
import { normalizeMultiSelectValue } from './utils/normalizeMultiSelectValue';

const MULTISELECT_LABEL_SELECTOR = '.p-multiselect-label';
const CHIP_SELECTOR = '.multi-select-chip';

interface MultiSelectProps {
  onChange: (value: string) => void;
  onTouch?: (value?: unknown) => void;
  onFocusIn?: () => void;
  onFocusOut?: () => void;
  value: unknown;
  error: FilterError;
  setError: (error: FilterError) => void;
  name: string;
  disabled?: boolean;
  chip?: boolean;
  options?: StaticControlItem[];
  // Lazy-load support: parent passes isLoading and onShow to defer dynamic option fetch until first popover open
  isLoading?: boolean;
  onShow?: () => void;
}

const MultiSelect = ({
  onChange,
  onTouch,
  onFocusIn,
  onFocusOut,
  value,
  name,
  error,
  setError,
  disabled,
  chip = true,
  options = [],
  isLoading = false,
  onShow,
}: MultiSelectProps) => {
  const { t } = useTranslate();
  /* Check if the multiselect label has horizontal scroll */
  const multiselectValue = useMemo(
    () => normalizeMultiSelectValue(value),
    [value]
  );
  const { containerRef, hasHorizontalScroll } = useChipsScrollbarDetection(
    multiselectValue.join(','),
    MULTISELECT_LABEL_SELECTOR,
    CHIP_SELECTOR
  );
  const numberOfSelectedValues = multiselectValue.length;
  const selectedItemsLabel = useMemo(
    () =>
      t('selectedItemsLabel').replace('{0}', numberOfSelectedValues.toString()),
    [numberOfSelectedValues, t]
  );

  const handleOnBlur = () => {
    if (error === null) {
      setError(null);
    }
    onFocusOut?.();
  };

  const submitValues = (values: string[]) => {
    const submitValue = values.length > 0 ? values.join(',') : '';
    onChange(submitValue);
    onTouch?.(submitValue);

    if (values.length !== 0) {
      setError(null);
    }
  };

  const handleOnChange = (e: MultiSelectChangeEvent) => {
    const selectedValues = e.value as string[];
    submitValues(selectedValues);
  };

  const handleOnRemove = (e: ChipRemoveEvent) => {
    const label = e.value;
    const valueToRemove = options.find((v) => v.label === label)?.value;
    const newValues = multiselectValue.filter((v) => v !== valueToRemove);
    submitValues(newValues);
    return true;
  };

  return (
    <div ref={containerRef} className="multi-select-container">
      <PrimeMultiSelect
        onBlur={handleOnBlur}
        onFocus={onFocusIn}
        onShow={onShow}
        emptyMessage={t(isLoading ? 'loading...' : 'noRecordsReport')}
        panelClassName="multi-select-panel"
        showClear={false}
        removeIcon={undefined}
        showSelectAll={false}
        placeholder={t('choose')}
        name={name}
        display={chip ? 'chip' : 'comma'}
        onChange={handleOnChange}
        options={options}
        optionLabel="label"
        optionValue="value"
        filter
        disabled={disabled}
        value={multiselectValue}
        invalid={!!error}
        maxSelectedLabels={MAX_SELECTED_LABELS_FOR_MULTISELECT}
        selectedItemsLabel={selectedItemsLabel}
        selectedItemTemplate={
          chip
            ? (itemValue: string) => (
                <Chip
                  key={itemValue}
                  value={itemValue}
                  options={options}
                  onRemove={handleOnRemove}
                  hasOverflow={hasHorizontalScroll}
                />
              )
            : undefined
        }
      />
      <InputErrorMessage error={error} />
    </div>
  );
};

export default MultiSelect;
